/* eslint-disable no-nested-ternary -- disable this rule for now */
/* eslint-disable react/no-array-index-key -- disable this rule for now */
import { Loader2Icon, XIcon } from "lucide-react";
import { Link } from "@repo/i18n/routing";
import { motion } from "motion/react";
import { Fragment, useEffect, useState } from "react";
import { ProductsRepository } from "@repo/fetch-repository/products";
import { ProjectsRepository } from "@repo/fetch-repository/projects";
import { ServicesRepository } from "@repo/fetch-repository/services";
import {
  SettingsRepository,
  type Social,
} from "@repo/fetch-repository/settings";
import { PagesRepository } from "@repo/fetch-repository/pages";
import { useRouter } from "next/router";
import { MediaRepository } from "@repo/fetch-repository/media";
import { useApp } from "@repo/app/useApp";
import { Button } from "../ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import { cn } from "../../libs/utils";
import { Triangle } from "../icons/triangle";

interface MenuProps {
  open: boolean;
  onChange: (val: boolean) => void;
}
interface MenuItem {
  title: string;
  href?: string;
  items?: MenuItem[];
}

interface MenuColumn {
  title: string;
  items?: MenuItem[];
  href?: string;
}

interface Menu {
  column1: MenuColumn[];
  column2: MenuColumn[];
  column3: MenuColumn[];
}

export function Menu({ onChange, open }: MenuProps): React.JSX.Element {
  const router = useRouter();
  const app = useApp();
  const locale =
    app.rendererMode === "editor"
      ? (router.query.locale as string)
      : router.locale;
  const [loading, setLoading] = useState(true);
  const [menuItems, setMenuItems] = useState<Menu | null>(null);
  const [socialItems, setSocialItems] = useState<Social[] | null>(null);
  const [socialItemsWithIcon, setSocialItemsWithIcon] = useState<
    | {
        name: string;
        link: string;
        icon?: string;
      }[]
    | null
  >(null);
  // fetch images for icon
  const mediaRepository = new MediaRepository(locale);
  useEffect(() => {
    if (!socialItems) return;

    const fetchIcons = async (): Promise<void> => {
      const withIcons = await Promise.all(
        socialItems.map(async (social) => ({
          link: social.link,
          name: social.name,
          icon: (await mediaRepository.get(social.icon)).data?.url,
        }))
      );

      setSocialItemsWithIcon(withIcons);
    };

    void fetchIcons();
  }, [socialItems]);
  useEffect(() => {
    if (open) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }
  }, [open]);
  useEffect(() => {
    const settingsRepository: SettingsRepository = new SettingsRepository(
      locale
    );
    const productsRepository: ProductsRepository = new ProductsRepository(
      locale
    );
    const projectsRepository: ProjectsRepository = new ProjectsRepository(
      locale
    );
    const servicesRepository: ServicesRepository = new ServicesRepository(
      locale
    );
    const pagesRepository: PagesRepository = new PagesRepository(locale);

    setLoading(true);
    void Promise.all([
      productsRepository.getMany({ per_page: 20 }),
      projectsRepository.getMany({ per_page: 20 }),
      servicesRepository.getMany({ per_page: 20 }),
      pagesRepository.getMany(),
      settingsRepository.getMany(),
    ])
      .then(([products, projects, services, pages, settings]) => {
        if (
          products.succeeded &&
          projects.succeeded &&
          services.succeeded &&
          pages.succeeded &&
          settings.succeeded
        ) {
          setSocialItems(
            settings.data?.find((setting) => setting.key === "socials")
              ?.value as Social[]
          );
          setMenuItems({
            column1: [
              {
                title: locale === "fa" ? "کاربرد ها" : "Applications",
                href: "/products",
                items: products.data
                  ? products.data.map((product) => ({
                      title: product.name,
                      href: `/products/${product.id}/${encodeURIComponent(
                        product.name.replace(/\s+/g, "-").toLowerCase()
                      )}/`,
                    }))
                  : [],
              },
              {
                title: locale === "fa" ? "خانه" : "Home",
                href: "/",
              },
              {
                title: locale === "fa" ? "پروژه ها" : "Projects",
                href: "/projects",
              },
              {
                title: locale === "fa" ? "تماس با ما" : "Contact Us",
                href: "/contact-us",
              },
              {
                title: locale === "fa" ? "کاتالوگ ها" : "Catalogs",
                href: "/catalogs",
              },
              {
                title: locale === "fa" ? "وبلاگ" : "Blog",
                href: "/posts",
              },
              {
                title: locale === "fa" ? "گالری" : "Gallery",
                href: "/gallery",
              },
              // ...(pages.data
              //   ? pages.data.map((page) => ({
              //       title: page.name,
              //       href: `/${page.link}`,
              //     }))
              //   : []),
            ],
            column2: [
              {
                title: locale === "fa" ? "درباره ما" : "About Company",
                href: "/about-company",
                items: [
                  {
                    title: locale === "fa" ? "تامین کنندگان" : "Suppliers",
                    href: "/suppliers",
                  },
                  {
                    title: locale === "fa" ? "نظرات" : "Reviews",
                    href: "/reviews",
                  },
                  {
                    title: locale === "fa" ? "تیم ما" : "Our Team",
                    href: "/teams",
                  },
                  {
                    title: locale === "fa" ? "تاریخچه" : "OUR HISTORY",
                    href: "/our%20history",
                  },
              {
                title: locale === "fa" ? "همکاری" : "CAREER",
                href: "/careers",
              },
                ],
              },
            ],
            column3: [
              {
                title: locale === "fa" ? "خدمات" : "Services",
                href: "/services",
                items: services.data
                  ? services.data.map((service) => ({
                      title: service.name,
                      href: `/services/${service.id}/${encodeURIComponent(
                        service.name.replace(/\s+/g, "-").toLowerCase()
                      )}/`,
                    }))
                  : [],
              },
            ],
          });
        }
      })
      .finally(() => {
        setLoading(false);
      });

    return () => {
      document.body.classList.remove("overflow-hidden"); // Clean up on unmount
    };
  }, [locale]);
  return (
    <>
      <motion.button
        animate={{ opacity: open ? 1 : 0 }}
        className={cn(
          "fixed top-0 left-0 bottom-0 right-0 z-40 cursor-pointer",
          !open ? "hidden" : ""
        )}
        exit={{ opacity: 0 }}
        initial={{ opacity: 0 }}
        onClick={() => {
          onChange(!open);
        }}
        transition={{ duration: 0.3 }}
      >
        <div
          className="absolute top-0 left-0 right-0 bottom-0 w-full h-full z-10"
          style={{
            backgroundColor: "#00000080",
            width: "100%",
            height: "100%",
          }}
        />
        <img
          alt="menu-bg"
          className="h-full object-cover z-0 w-full transform -translate-x-2/4"
          src="/images/header-bg.png"
        />
      </motion.button>
      <motion.div
        animate={{ opacity: open ? 1 : 0, translateX: open ? "0%" : "100%" }}
        className={cn(
          "fixed top-0  bottom-0 right-0 z-50 w-[calc(100vw-60px)] md:[max-width:calc(100vw-200px)] lg:[max-width:calc(100vw-300px)]",
          !open ? "hidden" : ""
        )}
        exit={{ opacity: 0, translateX: "100%", transition: { duration: 0.3 } }}
        initial={{ opacity: 0, translateX: "100%" }}
        transition={{ duration: 0.3 }}
      >
        <div className="absolute bottom-0 -left-[150px] w-full z-20 hidden lg:flex rtl:justify-end justify-start">
          <Triangle fill="#fff" />
        </div>
        <div
          className="absolute top-0 right-0 w-full bg-white h-screen   lg:px-20 p-7 flex flex-col overflow-auto"
          style={{
            scrollbarWidth: "none",
          }}
        >
          <div className="flex justify-end">
            <Button
              className="rounded-full"
              onClick={() => {
                onChange(!open);
              }}
              size="icon"
              variant="secondary"
            >
              <XIcon />
            </Button>
          </div>
          <div className="block lg:hidden">
            <Accordion className="w-full" collapsible type="single">
              {menuItems
                ? Object.values(menuItems).map(
                    (column: MenuColumn[], columnIndex: number) =>
                      column.map((item: MenuItem, itemIndex: number) =>
                        item.items ? (
                          <AccordionItem
                            key={`${columnIndex}-${itemIndex}`}
                            value={`item-${columnIndex}-${itemIndex}`}
                          >
                            <AccordionTrigger className="text-[18px] text-[#313131] uppercase rtl:font-doran rtl:font-bold">
                              {item.href ? (
                                <Link
                                  className="hover:text-[#f70] transition-colors 
                                duration-300"
                                  href={item.href}
                                  onClick={() => {
                                    setTimeout(() => {
                                      onChange(!open);
                                    }, 3000);
                                  }}
                                >
                                  {item.title}
                                </Link>
                              ) : (
                                item.title
                              )}
                            </AccordionTrigger>
                            <AccordionContent>
                              <div className="flex flex-col gap-2">
                                {item.items.map((subItem, subItemIndex) => (
                                  <Link
                                    className="hover:text-[#f70] transition-colors duration-300 rtl:font-doran rtl:font-normal"
                                    href={subItem.href}
                                    key={`${columnIndex}-${itemIndex}-${subItemIndex}`}
                                    onClick={() => {
                                      setTimeout(() => {
                                        onChange(!open);
                                      }, 3000);
                                    }}
                                  >
                                    {subItem.title}
                                  </Link>
                                ))}
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        ) : (
                          <Link
                            className="block text-[18px] text-[#313131] uppercase rtl:font-doran rtl:font-bold hover:text-[#f70] transition-colors duration-300 py-4 border-b border-[#313131] border-opacity-20"
                            href={item.href || "#"}
                            key={`${columnIndex}-${itemIndex}`}
                            onClick={() => {
                              setTimeout(() => {
                                onChange(!open);
                              }, 3000);
                            }}
                          >
                            {item.title}
                          </Link>
                        )
                      )
                  )
                : null}
              {socialItemsWithIcon && socialItemsWithIcon.length > 0 ? (
                <AccordionItem value="socials">
                  <div className="flex gap-2 py-4">
                    {socialItemsWithIcon.map((social, index) => (
                      <Link
                        href={social.link}
                        key={index}
                        target="_blank"
                        onClick={() => {
                          setTimeout(() => {
                            onChange(!open);
                          }, 3000);
                        }}
                      >
                        <img
                          alt={social.name}
                          className="rounded-full size-8"
                          src={social.icon}
                        />
                      </Link>
                    ))}
                  </div>
                </AccordionItem>
              ) : null}
            </Accordion>
          </div>
          {!loading ? (
            menuItems && Object.keys(menuItems).length > 0 ? (
              <div className="hidden lg:grid grid-cols-1 gap-10 lg:grid-cols-3 relative z-20">
                {Object.values(menuItems).map(
                  (column: MenuColumn[], columnIndex: number) => (
                    <div
                      className="flex flex-col lg:gap-4"
                      dir="auto"
                      key={columnIndex}
                    >
                      {column.map((item: MenuItem, itemIndex: number) => (
                        <Fragment key={itemIndex}>
                          <div className="flex flex-col lg:gap-4">
                            {item.href ? (
                              <Link
                                className="group relative text-xl py-1 hover:text-[#f70] transition-colors duration-300 rtl:font-doran rtl:font-medium font-normal uppercase"
                                href={item.href}
                                onClick={() => {
                                  setTimeout(() => {
                                    onChange(!open);
                                  }, 3000);
                                }}
                              >
                                <span className="absolute size-1 bg-primary ltr:-left-2.5 rtl:-right-2.5 top-0 opacity-0 group-hover:top-1/2 group-hover:opacity-100 transition-all ease-in-out duration-300" />
                                {item.title}
                              </Link>
                            ) : (
                              <h3 className="text-xl py-1 text-[#313131] uppercase rtl:font-doran font-normal rtl:font-medium">
                                {item.title}
                              </h3>
                            )}
                            {item.items ? (
                              <div className="flex flex-col gap-2 rtl:font-doran rtl:font-normal font-light text-base">
                                {item.items.map((subItem, subItemIndex) => (
                                  <Link
                                    className="group relative hover:text-[#f70] transition-colors duration-300"
                                    href={subItem.href}
                                    key={subItemIndex}
                                    onClick={() => {
                                      setTimeout(() => {
                                        onChange(!open);
                                      }, 3000);
                                    }}
                                  >
                                    <span className="absolute size-1 bg-primary ltr:-left-2.5 rtl:-right-2.5 top-0 opacity-0 group-hover:top-1/2 group-hover:opacity-100 transition-all ease-in-out duration-300" />
                                    {subItem.title}
                                  </Link>
                                ))}
                              </div>
                            ) : null}
                          </div>
                        </Fragment>
                      ))}
                      {columnIndex === 2 ? (
                        <div className="flex flex-col gap-10">
                          <div className="flex flex-col gap-4">
                            <h3 className="text-xl py-1 hover:text-[#f70] transition-colors duration-300 rtl:font-doran font-normal uppercase">
                              {locale === "fa"
                                ? "شبکه های اجتماعی"
                                : "Social Networks"}
                            </h3>
                            <div className="flex gap-4 lg:gap-3">
                              {socialItemsWithIcon?.map((social) => (
                                <Link
                                  href={social.link}
                                  key={social.name}
                                  onClick={() => {
                                    setTimeout(() => {
                                      onChange(!open);
                                    }, 3000);
                                  }}
                                  target="_blank"
                                >
                                  <div className="rounded-full size-10 lg:size-12 p-2">
                                    <img
                                      alt={social.name}
                                      className="size-full"
                                      src={social.icon}
                                      style={{
                                        mixBlendMode: "multiply",
                                      }}
                                    />
                                  </div>
                                </Link>
                              ))}
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  )
                )}
              </div>
            ) : null
          ) : (
            <div className="flex justify-center items-center h-full">
              <Loader2Icon className="animate-spin" />
            </div>
          )}
        </div>
      </motion.div>
    </>
  );
}
